import { useState, useEffect, useRef } from "react";

type GmvTab = "performance" | "targets";

/* ─── Animated counter ─── */
function AnimUp({ target, prefix = "", suffix = "" }: { target: number; prefix?: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<number>();
  useEffect(() => {
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min((now - start) / 1200, 1);
      setCount(Math.round(target * p));
      if (p < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => { if (ref.current) cancelAnimationFrame(ref.current); };
  }, [target]);
  return <>{prefix}{count.toLocaleString()}{suffix}</>;
}

/* ─── Performance Tab ─── */
const leaderboard = [
  { rank: 1, name: "Bella Permata", initial: "B", gmv: "220M", progress: 122, bonus: true },
  { rank: 2, name: "Sarah Amelia", initial: "S", gmv: "212M", progress: 118, bonus: true },
  { rank: 3, name: "Dina Kartika", initial: "D", gmv: "198M", progress: 110, bonus: true },
  { rank: 4, name: "Alya Putri", initial: "A", gmv: "174M", progress: 97, bonus: false },
  { rank: 5, name: "Dewi Anggraeni", initial: "D", gmv: "168M", progress: 93, bonus: false },
];

function PerformanceContent() {
  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Stats */}
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
        {[
          { label: "TOTAL GMV", value: <AnimUp target={1} prefix="IDR " suffix=".77B" /> },
          { label: "AVG GMV / HOST", value: <AnimUp target={147} prefix="IDR " suffix="M" /> },
          { label: "HOSTS ON TARGET", value: <><AnimUp target={4} /> / 12</> },
        ].map((s, i) => (
          <div key={i} className="p-4">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">{s.label}</div>
            <div className="text-[20px] font-semibold text-foreground mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="px-4 py-2.5 border-b border-border">
        <div className="text-[12px] font-medium text-foreground">Host Leaderboard</div>
      </div>
      <div className="flex-1 overflow-hidden">
        {leaderboard.map((h) => (
          <div key={h.rank} className="flex items-center gap-3 px-4 h-12 border-b border-border hover:bg-muted/30 transition-colors">
            <div className={`text-[12px] font-bold w-5 text-center ${h.rank <= 3 ? "text-success" : "text-muted-foreground"}`}>{h.rank}</div>
            <div className="h-6 w-6 rounded-full bg-muted-foreground/15 flex items-center justify-center text-[9px] font-medium text-muted-foreground shrink-0">{h.initial}</div>
            <span className="text-[11px] font-medium text-foreground flex-1 truncate">{h.name}</span>
            {h.bonus && <span className="text-[8px] px-1.5 py-0.5 rounded bg-success/15 text-success font-medium">🏆 Bonus</span>}
            <span className="text-[11px] font-semibold text-foreground w-20 text-right">IDR {h.gmv}</span>
            <div className="w-24 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-1000 ${h.progress >= 100 ? "bg-success" : h.progress >= 90 ? "bg-warning" : "bg-muted-foreground/40"}`}
                  style={{ width: `${Math.min(h.progress, 100)}%` }} />
              </div>
              <span className={`text-[10px] font-semibold w-8 text-right ${h.progress >= 100 ? "text-success" : h.progress >= 90 ? "text-warning" : "text-muted-foreground"}`}>{h.progress}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Targets Tab ─── */
function TargetsContent() {
  return (
    <div className="flex-1 flex flex-col min-w-0 p-4 gap-4">
      {/* Target card */}
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px]">🎯</span>
          <span className="text-[12px] font-semibold text-foreground">Monthly GMV Target</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[24px] font-semibold text-foreground">IDR 180M</span>
          <span className="text-[10px] text-muted-foreground">per host / month</span>
        </div>
        <div className="text-[9px] text-muted-foreground mt-2">Applies to all 12 hosts in the Wardah team</div>
      </div>

      {/* Bonus tiers */}
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px]">🏆</span>
          <span className="text-[12px] font-semibold text-foreground">Bonus Tiers</span>
        </div>
        <div className="space-y-2">
          {[
            { threshold: "100%", amount: "IDR 1M" },
            { threshold: "120%", amount: "IDR 2M" },
            { threshold: "150%", amount: "IDR 3M" },
          ].map((t, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="text-[11px] text-muted-foreground">≥ {t.threshold} of target</span>
              <span className="text-[12px] font-semibold text-success">{t.amount}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export function GmvPerformanceMockup() {
  const [activeTab, setActiveTab] = useState<GmvTab>("performance");

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab(c => c === "performance" ? "targets" : "performance");
    }, 12000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative" style={{ overflow: "visible" }}>
      <div className="relative z-10 rounded-t-xl border border-b-0 border-border bg-card overflow-hidden">
        <div className="flex flex-col min-h-[380px]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 h-10 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-foreground">Host GMV Performance</span>
              <span className="text-[9px] px-2 py-0.5 rounded bg-muted text-muted-foreground font-medium">Wardah</span>
            </div>
            <div className="flex items-center gap-0.5">
              {[
                { key: "performance" as GmvTab, label: "Performance", icon: "📊" },
                { key: "targets" as GmvTab, label: "Bonus Rules", icon: "⚙️" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-2.5 h-7 rounded text-[11px] transition-colors ${
                    activeTab === tab.key
                      ? "bg-accent text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  }`}
                >
                  <span className="text-[10px]">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="animate-fade-in" key={activeTab}>
            {activeTab === "performance" && <PerformanceContent />}
            {activeTab === "targets" && <TargetsContent />}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </div>
    </div>
  );
}
